import type { PoseFrame, Side } from '../types.js';
import { LM } from '../pose/landmarks.js';

/**
 * Wrist 2D position (x, y) per frame for the given side.
 * z is intentionally dropped — for swing-velocity peak detection the in-plane
 * motion dominates and avoids monocular-depth noise.
 */
export function wristXyTrack(
  frames: readonly PoseFrame[],
  side: Side,
): Array<readonly [number, number]> {
  const idx = side === 'right' ? LM.RIGHT_WRIST : LM.LEFT_WRIST;
  return frames.map((f) => {
    const lm = f[idx];
    return lm ? ([lm.x, lm.y] as const) : ([Number.NaN, Number.NaN] as const);
  });
}

/**
 * Linear (2D) speed of the wrist in normalized image units per second,
 * computed via central differences. Result length matches `track.length`;
 * the first and last samples reuse the nearest valid value.
 *
 * Use this as a lightweight proxy for "how fast the wrist is moving".
 */
export function wristSpeedSeries(
  track: ReadonlyArray<readonly [number, number]>,
  fps: number,
): number[] {
  const n = track.length;
  if (n < 2 || fps <= 0) return new Array(n).fill(0);

  const speeds = new Array<number>(n).fill(0);
  const dt = 1 / fps;

  for (let i = 1; i < n - 1; i++) {
    const prev = track[i - 1];
    const next = track[i + 1];
    if (!prev || !next) continue;
    const dx = (next[0] - prev[0]) / (2 * dt);
    const dy = (next[1] - prev[1]) / (2 * dt);
    speeds[i] = Math.hypot(dx, dy);
  }
  speeds[0] = speeds[1] ?? 0;
  speeds[n - 1] = speeds[n - 2] ?? 0;
  return speeds;
}

/**
 * Angular velocity (rad/s) of the elbow joint angle, computed from a precomputed
 * angle series (degrees). Output is in radians/second to be unit-consistent
 * with biomechanics literature.
 */
export function angularVelocityRadPerSec(
  angleSeriesDeg: readonly number[],
  fps: number,
): number[] {
  const n = angleSeriesDeg.length;
  if (n < 2 || fps <= 0) return new Array(n).fill(0);

  const out = new Array<number>(n).fill(0);
  const dt = 1 / fps;
  const degToRad = Math.PI / 180;

  for (let i = 1; i < n - 1; i++) {
    const prev = angleSeriesDeg[i - 1];
    const next = angleSeriesDeg[i + 1];
    if (prev === undefined || next === undefined || Number.isNaN(prev) || Number.isNaN(next)) {
      continue;
    }
    out[i] = ((next - prev) * degToRad) / (2 * dt);
  }
  out[0] = out[1] ?? 0;
  out[n - 1] = out[n - 2] ?? 0;
  return out;
}
